import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const FeatureInventoryPanel = withListOverflow(FeatureInventoryListOwner);
// src/features/admin/FeatureInventoryPanel.jsx
//
// ROADMAP-SIMPLIFY: Manual feature inventory. Status is set by hand via an
// inline dropdown per row. Admins can add and delete rows directly on the
// page. No sign-off, confirm, or override UI.
//
// ROADMAP-DEMO-FLAG-AND-STYLE: Demo star toggle + filter pill + §8/§15 style
// conformance.
//
// Design tokens (T.*) used throughout; no raw hex or px outside token map.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { Modal } from "../../components/Modal.jsx";
import { log } from "../../utils/logger.js";
import { matchesFoldedQuery } from "../../utils/textSearch.js";

// ─── Constants ─────────────────────────────────────────────────────────────────

const ALL_STATUSES = ["live", "partial", "mock", "behind_flag", "not_built", "fixing"];

const STATUS_META = {
  live:         { label: "Live",         color: T.statusLive,  bg: T.statusLiveBg  },
  partial:      { label: "Partial",      color: T.amber,       bg: T.utilAmber     },
  mock:         { label: "Mock",         color: T.statusMock,  bg: T.statusMockBg  },
  behind_flag:  { label: "Behind flag",  color: T.mid,         bg: T.lightGray     },
  not_built:    { label: "Not built",    color: T.errorRed,    bg: T.errorRedBg    },
  fixing:       { label: "Fixing",       color: T.amber,       bg: T.utilAmber     },
};

const FILTER_CHIPS = [
  { key: "all", label: "All" },
  ...ALL_STATUSES.map((s) => ({ key: s, label: STATUS_META[s].label })),
];

const COLS = {
  demo:     "36px",
  feature:  "280px",
  location: "220px",
  status:   "130px",
  notes:    "1fr",
  actions:  "90px",
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

// §8 KPI Card Row — white + hairline border, radius 11, padding 12/15,
// value 21/600 (T.fsLgX ≈ 20, closest token), label 9.5/700 uppercase T.mid.
function SummaryCard({ status, count, active, onClick }) {
  const meta = STATUS_META[status];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`summary-card-${status}`}
      style={{
        display:        "flex",
        flexDirection:  "column",
        alignItems:     "flex-start",
        justifyContent: "center",
        gap:            4,
        padding:        "12px 15px",
        borderRadius:   11,
        background:     active ? meta.bg : T.card,
        border:         `1px solid ${active ? meta.color : T.line}`,
        cursor:         "pointer",
        minWidth:       90,
        minHeight:      78,
        flex:           1,
        outline:        "none",
        fontFamily:     "inherit",
        textAlign:      "left",
        transition:     "border-color 0.12s",
      }}
    >
      <span style={{ fontSize: T.fsLgX, fontWeight: T.fwSemibold, color: meta.color }}>
        {count}
      </span>
      <span style={{ fontSize: T.fsXxxs5, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: 0.3 }}>
        {meta.label}
      </span>
    </button>
  );
}

// ─── Inline status dropdown — §15 Inline Selector conformance ─────────────────
// Background white, 1px T.line border, 12.5/600/T.dark text. No raw hex.

function StatusSelect({ rowId, value, onChange, busy }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(rowId, e.target.value)}
      disabled={busy}
      data-testid={`status-select-${rowId}`}
      style={{
        fontSize:     T.fsSm5,
        fontWeight:   T.fwSemibold,
        color:        T.dark,
        background:   T.white,
        border:       `1px solid ${T.line}`,
        borderRadius: 7,
        padding:      "4px 10px",
        cursor:       busy ? "not-allowed" : "pointer",
        fontFamily:   "inherit",
        outline:      "none",
      }}
    >
      {ALL_STATUSES.map((s) => (
        <option key={s} value={s}>{STATUS_META[s].label}</option>
      ))}
    </select>
  );
}

// ─── Inline editable cell ──────────────────────────────────────────────────────

function EditableCell({ rowId, field, value, multiline, onSave, busy, testIdPrefix }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value || "");

  const handleBlur = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== (value || "").trim()) {
      onSave(rowId, field, trimmed);
    }
  }, [rowId, field, draft, value, onSave]);

  if (editing) {
    const sharedStyle = {
      fontSize:   T.fsSm,
      color:      T.dark,
      border:     `1px solid ${T.blue}`,
      borderRadius: 4,
      padding:    "3px 6px",
      fontFamily: "inherit",
      outline:    "none",
      width:      "100%",
      background: T.white,
    };
    return multiline ? (
      <textarea
        autoFocus
        rows={2}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        disabled={busy}
        data-testid={testIdPrefix ? `${testIdPrefix}-${rowId}` : undefined}
        style={{ ...sharedStyle, resize: "vertical", minHeight: 40 }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        disabled={busy}
        data-testid={testIdPrefix ? `${testIdPrefix}-${rowId}` : undefined}
        style={sharedStyle}
      />
    );
  }

  return (
    <span
      onClick={() => { if (!busy) { setDraft(value || ""); setEditing(true); } }}
      style={{
        display:  "block",
        fontSize: T.fsSm,
        color:    value ? T.dark : T.light,
        cursor:   busy ? "default" : "text",
        minHeight: 20,
        borderRadius: 4,
        padding:  "2px 4px",
        marginLeft: -4,
      }}
    >
      {value || <em style={{ color: T.light }}>—</em>}
    </span>
  );
}

// ─── Add feature modal form ────────────────────────────────────────────────────

function AddFeatureRow({ onSubmit, onCancel, busy }) {
  const [name,     setName]     = useState("");
  const [area,     setArea]     = useState("");
  const [location, setLocation] = useState("");
  const [status,   setStatus]   = useState("not_built");
  const [notes,    setNotes]    = useState("");
  const [err,      setErr]      = useState(null);

  const handleSubmit = () => {
    if (!name.trim())     { setErr("Name is required.");     return; }
    if (!area.trim())     { setErr("Area is required.");     return; }
    if (!location.trim()) { setErr("Location is required."); return; }
    setErr(null);
    onSubmit({ name: name.trim(), area: area.trim(), location: location.trim(), status, notes: notes.trim() });
  };

  const inputStyle = {
    fontSize: T.fsSm, color: T.dark, border: `1px solid ${T.border}`,
    borderRadius: 4, padding: "6px 9px", fontFamily: "inherit",
    background: T.white, outline: "none", width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: T.fsXs,
    fontWeight: T.fwSemibold,
    color: T.mid,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    marginBottom: 4,
  };

  const fieldStyle = { marginBottom: 14 };

  return (
    <div data-testid="add-feature-row" style={{ padding: "20px 20px 0" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Feature name *</label>
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => { setName(e.target.value); setErr(null); }}
            placeholder="e.g. Lease comparison"
            data-testid="add-feature-name"
            style={{ ...inputStyle, border: err && !name.trim() ? `1px solid ${T.errorRed}` : inputStyle.border }}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Location *</label>
          <input
            type="text"
            value={location}
            onChange={(e) => { setLocation(e.target.value); setErr(null); }}
            placeholder="e.g. Lease tab"
            data-testid="add-feature-location"
            style={{ ...inputStyle, border: err && !location.trim() ? `1px solid ${T.errorRed}` : inputStyle.border }}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Area *</label>
          <input
            type="text"
            value={area}
            onChange={(e) => { setArea(e.target.value); setErr(null); }}
            placeholder="e.g. Leasing"
            data-testid="add-feature-area"
            style={{ ...inputStyle, border: err && !area.trim() ? `1px solid ${T.errorRed}` : inputStyle.border }}
          />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="add-feature-status"
            style={{ ...inputStyle }}
          >
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 4 }}>
        <label style={labelStyle}>Notes</label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
          data-testid="add-feature-notes"
          style={inputStyle}
        />
      </div>

      {err && (
        <div style={{ fontSize: T.fsXs, color: T.errorRed, marginTop: 8 }}>{err}</div>
      )}

      <div style={{
        display: "flex", justifyContent: "flex-end", gap: 8,
        borderTop: `1px solid ${T.border}`, margin: "16px -20px 0",
        padding: "12px 20px",
      }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="add-feature-cancel"
          style={{
            fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.mid,
            background: T.white, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: "7px 16px", cursor: busy ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy}
          data-testid="add-feature-submit"
          style={{
            fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.white,
            background: T.blue, border: "none", borderRadius: 6,
            padding: "7px 16px", cursor: busy ? "not-allowed" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {busy ? "Saving…" : "Add feature"}
        </button>
      </div>
    </div>
  );
}

// ─── Table row ────────────────────────────────────────────────────────────────

function FeatureRow({ row, onStatusChange, onFieldEdit, onDelete, onDemoToggle, busy }) {
  const isDemo = Boolean(row.is_demo);
  const [hovered,        setHovered]        = useState(false);
  const [iconHovered,    setIconHovered]    = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);

  return (
    <tr
      data-testid={`feature-row-${row.id}`}
      style={{ borderBottom: `1px solid ${T.border}` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setIconHovered(false); }}
    >
      {/* Star / demo toggle — first column */}
      <td style={{ padding: "10px 6px", verticalAlign: "top", textAlign: "center" }}>
        <button
          type="button"
          onClick={() => onDemoToggle(row.id)}
          disabled={busy}
          data-testid={`demo-star-${row.id}`}
          aria-label={isDemo ? "Remove demo flag" : "Mark as demo-ready"}
          title={isDemo ? "Demo-ready (click to remove)" : "Mark as demo-ready"}
          style={{
            fontSize:   T.fsBase,
            lineHeight: 1,
            color:      isDemo ? T.amber : T.light,
            background: "none",
            border:     "none",
            padding:    "2px 4px",
            cursor:     busy ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            outline:    "none",
            transition: "color 0.12s",
          }}
        >
          {isDemo ? "★" : "☆"}
        </button>
      </td>

      <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
        <EditableCell
          rowId={row.id}
          field="name"
          value={row.name}
          onSave={onFieldEdit}
          busy={busy}
          testIdPrefix="edit-name"
        />
      </td>

      <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
        <EditableCell
          rowId={row.id}
          field="location"
          value={row.location}
          onSave={onFieldEdit}
          busy={busy}
          testIdPrefix="edit-location"
        />
      </td>

      <td style={{ padding: "10px 12px", verticalAlign: "top", whiteSpace: "nowrap" }}>
        <StatusSelect
          rowId={row.id}
          value={row.status}
          onChange={onStatusChange}
          busy={busy}
        />
      </td>

      <td style={{ padding: "10px 12px", verticalAlign: "top" }}>
        <EditableCell
          rowId={row.id}
          field="notes"
          value={row.notes}
          multiline
          onSave={onFieldEdit}
          busy={busy}
          testIdPrefix="edit-notes"
        />
      </td>

      <td style={{ padding: "10px 12px", verticalAlign: "top", whiteSpace: "nowrap" }}>
        {confirmPending ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              onClick={() => { onDelete(row.id); setConfirmPending(false); }}
              disabled={busy}
              data-testid={`confirm-btn-${row.id}`}
              style={{
                fontSize:   T.fsXs,
                fontWeight: T.fwSemibold,
                color:      T.errorRed,
                background: "none",
                border:     "none",
                cursor:     busy ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                padding:    "2px 0",
              }}
            >
              Delete?
            </button>
            <span style={{ color: T.light, fontSize: T.fsXs }}>·</span>
            <button
              type="button"
              onClick={() => setConfirmPending(false)}
              data-testid={`cancel-btn-${row.id}`}
              style={{
                fontSize:   T.fsXs,
                color:      T.mid,
                background: "none",
                border:     "none",
                cursor:     "pointer",
                fontFamily: "inherit",
                padding:    "2px 0",
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmPending(true)}
            disabled={busy}
            data-testid={`delete-btn-${row.id}`}
            title="Delete"
            onMouseEnter={() => setIconHovered(true)}
            onMouseLeave={() => setIconHovered(false)}
            style={{
              background:  "none",
              border:      "none",
              padding:     "2px 4px",
              cursor:      busy ? "not-allowed" : "pointer",
              color:       iconHovered ? T.red : T.light,
              opacity:     hovered ? 1 : 0,
              transition:  "opacity 0.13s, color 0.12s",
              lineHeight:  1,
              display:     "inline-flex",
              alignItems:  "center",
            }}
          >
            <svg
              width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

function FeatureInventoryListOwner() {
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [busy,         setBusy]         = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [demoOnly,     setDemoOnly]     = useState(false);
  const [search,       setSearch]       = useState("");
  const [showAddForm,  setShowAddForm]  = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch("/api/admin/feature-inventory")
      .then((r) => {
        if (!r.ok) throw new Error(`Server responded ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setRows(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        log.error("FeatureInventoryPanel: load failed", { err: String(err) });
        setError("Failed to load feature inventory.");
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  // ─── Counts per status ──────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = {};
    for (const s of ALL_STATUSES) c[s] = 0;
    for (const r of rows) {
      if (r.status in c) c[r.status]++;
    }
    return c;
  }, [rows]);

  // ─── Filtered rows ──────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let out = rows;
    if (activeFilter !== "all") {
      out = out.filter((r) => r.status === activeFilter);
    }
    if (demoOnly) {
      out = out.filter((r) => r.is_demo);
    }
    if (search.trim()) {
      const q = search.trim();
      out = out.filter((r) =>
        matchesFoldedQuery(q, r.name, r.area, r.location, r.notes)
      );
    }
    return out;
  }, [rows, activeFilter, demoOnly, search]);

  // ─── Group by area ──────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const map = {};
    for (const r of filtered) {
      if (!map[r.area]) map[r.area] = [];
      map[r.area].push(r);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // ─── Status change ───────────────────────────────────────────────────────
  const handleStatusChange = useCallback(async (id, status) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/feature-inventory/${id}/status`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      const updated = await res.json();
      setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r));
    } catch (err) {
      log.error("FeatureInventoryPanel: status change failed", { id, err: String(err) });
      setError(err.message || "Status update failed.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // ─── Demo toggle ─────────────────────────────────────────────────────────
  const handleDemoToggle = useCallback(async (id) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    // Optimistic update
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, is_demo: !r.is_demo } : r));
    try {
      const res = await fetch(`/api/admin/feature-inventory/${id}/demo`, {
        method: "PATCH",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      const result = await res.json();
      setRows((prev) => prev.map((r) => r.id === result.id ? { ...r, is_demo: result.is_demo } : r));
    } catch (err) {
      // Revert optimistic update on failure
      setRows((prev) => prev.map((r) => r.id === id ? { ...r, is_demo: !r.is_demo } : r));
      log.error("FeatureInventoryPanel: demo toggle failed", { id, err: String(err) });
      setError(err.message || "Demo toggle failed.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // ─── Field edit (name / location / notes) ───────────────────────────────
  const handleFieldEdit = useCallback(async (id, field, value) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/feature-inventory/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ [field]: value }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      const updated = await res.json();
      setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r));
    } catch (err) {
      log.error("FeatureInventoryPanel: field edit failed", { id, field, err: String(err) });
      setError(err.message || "Edit failed.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // ─── Delete ─────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (id) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/feature-inventory/${id}`, {
        method: "DELETE",
      });
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      log.error("FeatureInventoryPanel: delete failed", { id, err: String(err) });
      setError(err.message || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // ─── Add row ─────────────────────────────────────────────────────────────
  const handleAddSubmit = useCallback(async (fields) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/feature-inventory", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(fields),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server responded ${res.status}`);
      }
      const newRow = await res.json();
      setRows((prev) => [...prev, newRow]);
      setShowAddForm(false);
    } catch (err) {
      log.error("FeatureInventoryPanel: add failed", { err: String(err) });
      setError(err.message || "Add failed.");
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div data-testid="feature-inventory-loading" style={{ color: T.mid, fontSize: T.fsBase }}>
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="feature-inventory-panel" style={{ maxWidth: "1400px", padding: "24px 32px 40px", margin: "0 auto", width: "100%" }}>
      {error && (
        <ErrorBanner error={error} onDismiss={() => setError(null)} />
      )}

      {/* Summary cards — §8 KPI Card Row */}
      <div
        data-testid="summary-cards"
        style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}
      >
        {ALL_STATUSES.map((s) => (
          <SummaryCard
            key={s}
            status={s}
            count={counts[s]}
            active={activeFilter === s}
            onClick={() => setActiveFilter((f) => f === s ? "all" : s)}
          />
        ))}
      </div>

      {/* Filter chips + Demo pill + search + add button */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {FILTER_CHIPS.map(({ key, label }) => {
          const isActive = activeFilter === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`filter-chip-${key}`}
              onClick={() => setActiveFilter((prev) => (key !== "all" && prev === key) ? "all" : key)}
              style={{
                fontSize:   T.fsSm,
                fontWeight: T.fwSemibold,
                color:      isActive ? T.blue : T.mid,
                background: isActive ? T.blueSoft : T.white,
                border:     `1px solid ${isActive ? T.blue : T.border}`,
                borderRadius: 999,
                padding:    "4px 12px",
                cursor:     "pointer",
                fontFamily: "inherit",
                outline:    "none",
              }}
            >
              {label}
            </button>
          );
        })}

        {/* Demo filter pill */}
        <button
          type="button"
          data-testid="filter-chip-demo"
          onClick={() => setDemoOnly((prev) => !prev)}
          style={{
            fontSize:   T.fsSm,
            fontWeight: T.fwSemibold,
            color:      demoOnly ? T.amber : T.mid,
            background: demoOnly ? T.utilAmber : T.white,
            border:     `1px solid ${demoOnly ? T.amber : T.border}`,
            borderRadius: 999,
            padding:    "4px 12px",
            cursor:     "pointer",
            fontFamily: "inherit",
            outline:    "none",
          }}
        >
          ★ Demo
        </button>

        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search features…"
          data-testid="feature-search"
          style={{
            fontSize:    T.fsSm,
            color:       T.dark,
            border:      `1px solid ${T.border}`,
            borderRadius: 6,
            padding:     "5px 10px",
            outline:     "none",
            minWidth:    180,
            fontFamily:  "inherit",
          }}
        />

        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          disabled={busy}
          data-testid="add-feature-btn"
          style={{
            marginLeft:  "auto",
            fontSize:    T.fsSm,
            fontWeight:  T.fwSemibold,
            color:       T.white,
            background:  T.blue,
            border:      "none",
            borderRadius: 6,
            padding:     "6px 14px",
            cursor:      busy ? "not-allowed" : "pointer",
            fontFamily:  "inherit",
          }}
        >
          + Add feature
        </button>
      </div>

      {/* Grouped table */}
      {grouped.length === 0 ? (
        <div style={{ color: T.mid, fontSize: T.fsBase, padding: "24px 0" }}>
          No features match the current filter.
        </div>
      ) : (
        grouped.map(([area, areaRows]) => (
          <div key={area} style={{ marginBottom: 28 }}>
            <h3 style={{
              margin: "0 0 8px",
              fontSize: T.fsMd,
              fontWeight: T.fwBold,
              color: T.dark,
              letterSpacing: -0.2,
            }}>
              {area}
              <span style={{ fontWeight: T.fwRegular, color: T.light, fontSize: T.fsSm, marginLeft: 8 }}>
                {areaRows.length} feature{areaRows.length !== 1 ? "s" : ""}
              </span>
            </h3>

            <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflowX: "auto" }}>
              <table
                style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
                data-testid={`area-table-${area.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
              >
                <colgroup>
                  <col style={{ width: COLS.demo }} />
                  <col style={{ width: COLS.feature }} />
                  <col style={{ width: COLS.location }} />
                  <col style={{ width: COLS.status }} />
                  <col />
                  <col style={{ width: COLS.actions }} />
                </colgroup>
                <thead>
                  <tr style={{ background: T.lightGray }}>
                    {["", "Feature", "Location", "Status", "Notes", ""].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          padding:   i === 0 ? "8px 6px" : "8px 12px",
                          textAlign: "left",
                          fontSize:  T.fsXs,
                          fontWeight: T.fwBold,
                          color:     T.mid,
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                          borderBottom: `1px solid ${T.border}`,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ background: T.white }}>
                  {areaRows.map((row) => (
                    <FeatureRow
                      key={row.id}
                      row={row}
                      onStatusChange={handleStatusChange}
                      onFieldEdit={handleFieldEdit}
                      onDelete={handleDelete}
                      onDemoToggle={handleDemoToggle}
                      busy={busy}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      <Modal
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        title="Add feature"
        maxWidth={680}
        testId="add-feature-modal"
        closeTestId="add-feature-modal-close"
        ariaLabelledBy="add-feature-modal-title"
      >
        <AddFeatureRow
          onSubmit={handleAddSubmit}
          onCancel={() => setShowAddForm(false)}
          busy={busy}
        />
      </Modal>
    </div>
  );
}
